
import { Modal } from 'bootstrap';
import { defineComponent, ref, inject, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Apollo, Notify } from '@/core/services';
import { Actions } from '@/store/enums/StoreEnums';
import { setPageHistory } from '@/core/helpers/toolbar';
import { useI18n } from 'vue-i18n';
import { GET_PRODUCT_GROUP_CUSTOMERS } from '@/modules/catalog/product-groups/graphql/Queries';
import Table from '@/components/Table/Table.vue';

const PER_PAGE = 10;

export default defineComponent({
    name: 'product group customers',
    components: {
        Table
    },

    setup() {
        const emitter: any = inject('emitter');
        const router = useRouter();
        const store = useStore();
        const i18n = useI18n();
        const modal: any = ref();
        const loader = ref(false);
        const productGroupId = ref();
        const groupName = ref('');
        const searchKey = ref('');
        const customers = ref([]) as Record<any, any>;
        const pagination = ref({}) as Record<any, any>;

        const columns = ref([
            {
                label: 'message.NAME',
                key: 'name'
            },
            {
                label: 'message.EMAIL',
                key: 'email'
            },
            {
                label: 'message.ACCOUNT_ECONOMIC_NUMBER',
                key: 'economic_number',
                width: true
            },
            {
                label: 'message.SITE',
                key: 'site',
                width: true
            }
        ]);

        // the API decides by the group's source: shop group = customers.product_group_id, Smart group = customers.wms_product_group_id
        const getCustomers = (page = 1) => {
            loader.value = true;
            Apollo.query({
                query: GET_PRODUCT_GROUP_CUSTOMERS,
                fetchPolicy: 'network-only',
                variables: {
                    id: Number(productGroupId.value),
                    page: page,
                    limit: PER_PAGE,
                    search_key: searchKey.value || null
                }
            })
                .then(({ data }) => {
                    const response = JSON.parse(data.product_group_customers);
                    customers.value = response.data || [];
                    pagination.value = {
                        total: response.total,
                        current_page: response.current_page,
                        last_page: response.last_page,
                        per_page: PER_PAGE
                    };
                })
                .catch(() => Notify.error(i18n.t('message.SOMETHING_WENT_WRONG')))
                .finally(() => (loader.value = false));
        };

        const modalElement = () => document.getElementById('modal_product_group_customers') as HTMLElement | null;

        // Customers list filtered to this customer (by email, else customer number, else name), the way a search on
        // that page leaves it: the filter boxes come from the page history, the search from the search data
        const showInCustomersList = (customer: Record<any, any>) => {
            const filter = customer.email
                ? { email: customer.email }
                : customer.economic_number
                ? { economic_number: customer.economic_number }
                : { name: customer.name };
            setPageHistory({ filter: filter, search_key: '', page_number: 1 });
            store.dispatch(Actions.SEARCH_HANDLER, { data: { input: '', select: filter } });
            return router.push({ name: 'customers_list' });
        };

        // the Customers list, once the modal has finished closing: leaving the page during its fade would leave
        // Bootstrap's backdrop (and the blurred page) behind
        const openCustomer = (customer: Record<any, any>) => {
            const goToCustomer = () => showInCustomersList(customer);
            const element = modalElement();
            if (modal.value && element && element.classList.contains('show')) {
                element.addEventListener('hidden.bs.modal', goToCustomer, { once: true });
                modal.value.hide();
            } else {
                goToCustomer();
            }
        };

        const openModal = (product_group: Record<any, any>) => {
            productGroupId.value = product_group.id;
            groupName.value = product_group.name;
            searchKey.value = '';
            customers.value = [];
            pagination.value = {};
            // one instance for the element (a new one per open would stack backdrops); Bootstrap 5.0.1 has no getOrCreateInstance
            const element = modalElement() as HTMLElement;
            modal.value = Modal.getInstance(element) || new Modal(element);
            modal.value.show();
            getCustomers(1);
        };

        // leaving the page while the modal is open (e.g. browser back): remove what Bootstrap added to the page
        const removeModalLeftovers = () => {
            const element = modalElement();
            if (element && modal.value) {
                modal.value.dispose();
            }
            document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        };

        emitter.on('productGroupCustomersAction', openModal);
        onUnmounted(() => {
            emitter.off('productGroupCustomersAction', openModal);
            if (modal.value) {
                removeModalLeftovers();
            }
        });

        return {
            columns,
            customers,
            pagination,
            loader,
            groupName,
            searchKey,
            getCustomers,
            openCustomer
        };
    }
});
